import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import '../../styles/main.scss';
import api from '../../services/api';
import { readAttachmentAsText } from '../../utils/attachmentText';
import {
    MODULE_TYPES,
    resolveChatModuleConfig,
} from '../../constants/chatModuleConfig';
import {
    buildOnboardingMarkdown,
    createOnboardingChatMessage,
    ONBOARDING_MESSAGE_ID,
} from '../../constants/onboardingMessages';
import { PRODUCT_NAMES, VALIDATION_LIMITS } from '../../constants/validationLimits';
import {
    buildBrdPrefillCommand,
    validateGenerationCommand,
} from '../../utils/commandValidation';
import { useClerkSessionReady } from '../../hooks/useClerkSessionReady';
import { IoSend } from "react-icons/io5";
import { IoClose } from "react-icons/io5";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import { PaperClipIcon, DocumentDownloadIcon } from '@heroicons/react/solid';
// import ConversionCheckpoints from './ConversionCheckpoints';
// import TestCases from './TestCases';
// import Artifacts from './Artifacts';

const MAX_CHAT_ATTACHMENTS = 5;
const MAX_CHAT_FILE_BYTES = 250 * 1024;
/** Project .zip uploads (base64 to API); must match backend MAX_ZIP_SIZE_MB. */
const MAX_CHAT_ZIP_BYTES = VALIDATION_LIMITS.maxZipSizeMb * 1024 * 1024;

function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            if (typeof dataUrl !== "string" || !dataUrl.includes(",")) {
                reject(new Error("Could not read file"));
                return;
            }
            resolve(dataUrl.split(",")[1] || "");
        };
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsDataURL(file);
    });
}

/**
 * Save text to the user's machine. Uses the system "Save as" dialog when supported (Chrome/Edge),
 * otherwise triggers a normal browser download (typically to the Downloads folder).
 */
async function saveTextToLocalDisk(text, suggestedName) {
    const base =
        (suggestedName || "file")
            .replace(/[<>:"|?*]/g, "_")
            .replace(/\\/g, "_")
            .trim()
            .slice(0, 180) || "file";
    const hasReasonableExt = /\.[A-Za-z0-9]{1,12}$/.test(base);
    const withExt = hasReasonableExt ? base : `${base}.txt`;
    const payload = typeof text === "string" ? text : String(text);

    if (typeof window !== "undefined" && typeof window.showSaveFilePicker === "function") {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: withExt,
            });
            const writable = await handle.createWritable();
            await writable.write(new Blob([payload], { type: "text/plain;charset=utf-8" }));
            await writable.close();
            return;
        } catch (err) {
            if (err && err.name === "AbortError") return;
            console.warn("showSaveFilePicker failed, using download fallback", err);
        }
    }

    const blob = new Blob([payload], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = withExt;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/** Split assistant text into prose segments and ``` fenced code blocks (downloadable files). */
function parseMarkdownFencedBlocks(text) {
    if (!text || typeof text !== "string") return [{ type: "text", value: "" }];
    const parts = [];
    const re = /```([^\n`]*)\n([\s\S]*?)```/g;
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) {
            parts.push({ type: "text", value: text.slice(last, m.index) });
        }
        const lang = (m[1] || "").trim();
        const body = (m[2] || "").replace(/\n$/, "");
        parts.push({ type: "code", lang, body });
        last = m.index + m[0].length;
    }
    if (last < text.length) {
        parts.push({ type: "text", value: text.slice(last) });
    }
    if (parts.length === 0) {
        parts.push({ type: "text", value: text });
    }
    return parts;
}

/** Remove chat lines and inline snippets that repeat paths already shown as download rows. */
function stripArtifactPathsFromAssistantText(text, files) {
    if (!text || typeof text !== 'string' || !Array.isArray(files) || files.length === 0) {
        return text;
    }
    const paths = [...new Set(files.map((f) => f.path).filter(Boolean))];
    if (paths.length === 0) return text;
    const lineMentionsPath = (line, p) => {
        if (!p) return false;
        const a = line.replace(/\\/g, '/');
        const b = p.replace(/\\/g, '/');
        return a.includes(b);
    };
    const lines = text.split('\n');
    const kept = lines.filter((line) => {
        const t = line.trim();
        if (!t) return true;
        return !paths.some((p) => lineMentionsPath(line, p));
    });
    let out = kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    for (const p of paths) {
        if (!p) continue;
        const a = out.replace(/\\/g, '/');
        const b = p.replace(/\\/g, '/');
        if (a.includes(b)) {
            out = out.split(p).join('');
            out = out.split(p.replace(/\\/g, '/')).join('');
        }
    }
    return out.replace(/\n{3,}/g, '\n\n').trim();
}

function tryParseLeadingJson(text) {
    if (!text || typeof text !== 'string') return null;
    const t = text.trim();
    if (!t) return null;
    try {
        return JSON.parse(t);
    } catch {
        const s = t.indexOf('{');
        const e = t.lastIndexOf('}');
        if (s < 0 || e <= s) return null;
        try {
            return JSON.parse(t.slice(s, e + 1));
        } catch {
            return null;
        }
    }
}

function collectArtifactPathsFromObject(obj, outSet) {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
        obj.forEach((x) => collectArtifactPathsFromObject(x, outSet));
        return;
    }
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'string' && v.trim()) {
            const looksAbs =
                /^[A-Za-z]:[\\/]/.test(v) ||
                v.startsWith('\\\\') ||
                (v.startsWith('/') && !v.startsWith('//'));
            if (
                looksAbs &&
                (k === 'output_path' ||
                    k === 'output_file' ||
                    k === 'local_path' ||
                    k === 'saved_path' ||
                    k === 'schema_json_path' ||
                    (k.endsWith('_path') && !k.toLowerCase().includes('url')))
            ) {
                outSet.add(v.trim());
            }
        } else if (v && typeof v === 'object') {
            collectArtifactPathsFromObject(v, outSet);
        }
    }
}

const BRD_PUBLIC_ARTIFACT_FILENAMES = new Set(['brd.md', 'traceability_report.md']);

function filterGeneratedFilesForModule(files, moduleType) {
    if (moduleType !== MODULE_TYPES.BRD || !Array.isArray(files)) {
        return files;
    }
    return files.filter((f) =>
        BRD_PUBLIC_ARTIFACT_FILENAMES.has((f.filename || '').toLowerCase())
    );
}

function tryExtractGeneratedFilesFromAssistantText(text, moduleType = MODULE_TYPES.TECH_DOC) {
    const parsed = tryParseLeadingJson(text);
    if (!parsed) return [];
    const paths = new Set();
    collectArtifactPathsFromObject(parsed, paths);
    const files = [...paths].map((path) => ({
        path,
        filename: path.replace(/\\/g, '/').split('/').filter(Boolean).pop() || 'file',
        tool: '',
    }));
    return filterGeneratedFilesForModule(files, moduleType);
}

function looksLikeToolResultJson(parsed) {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    if (typeof parsed.status !== 'string' || !parsed.status.trim()) return false;
    const keys = Object.keys(parsed);
    return (
        keys.some((k) => k.endsWith('_path')) ||
        parsed.output_path != null ||
        parsed.mermaid_code != null ||
        parsed.diagram_type != null ||
        parsed.prompt_name != null ||
        parsed.form_name != null ||
        parsed.tool != null ||
        typeof parsed.message === 'string' ||
        typeof parsed.error === 'string'
    );
}

function outcomeLabelFromToolStatus(statusRaw) {
    const s = String(statusRaw || '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_');
    const success = new Set(['SUCCESS', 'PARTIAL_SUCCESS', 'OK', 'DONE', 'COMPLETED', 'COMPLETE']);
    const failure = new Set([
        'ERROR',
        'FAILURE',
        'FAILED',
        'PARTIAL_ERROR',
        'PARTIAL_FAILURE',
        'UNAVAILABLE',
        'WARNING',
        'LIMIT_REACHED',
        'LINE_LIMIT_EXCEEDED',
        'AUTH_REQUIRED',
        'ZIP_FILE_COUNT_EXCEEDED',
        'ZIP_SIZE_EXCEEDED',
        'ZIP_EMPTY',
    ]);
    if (success.has(s)) return 'SUCCESS';
    if (failure.has(s)) return 'ERROR';
    // Unknown / ambiguous statuses: treat as failure for the banner (safer than claiming success).
    return 'ERROR';
}

function formatGenerationRestrictionMessage(parsed, label, moduleType = MODULE_TYPES.TECH_DOC) {
    const status = String(parsed?.status || '').toUpperCase();
    const system = String(parsed?.system || '').toUpperCase();
    const isBrd =
        system === 'BRD' || moduleType === MODULE_TYPES.BRD;
    const productName = isBrd ? PRODUCT_NAMES.BRD : PRODUCT_NAMES.TECH_DOC;
    const lines = [];

    if (typeof parsed?.message === 'string' && parsed.message.trim()) {
        lines.push(parsed.message.trim());
    } else if (status === 'ZIP_FILE_COUNT_EXCEEDED') {
        lines.push(
            `You can upload a ZIP containing a maximum of ${VALIDATION_LIMITS.maxZipFileCount} files for ${productName}.`,
        );
    } else if (status === 'ZIP_SIZE_EXCEEDED') {
        lines.push(
            `The uploaded ZIP exceeds the maximum allowed size of ${VALIDATION_LIMITS.maxZipSizeMb} MB for ${productName}.`,
        );
    } else if (status === 'ZIP_EMPTY') {
        lines.push(
            `The uploaded ZIP does not contain any files for ${productName}. Add project content and try again.`,
        );
    } else if (status === 'LINE_LIMIT_EXCEEDED' && !isBrd) {
        const max =
            parsed.max_lines != null
                ? Number(parsed.max_lines)
                : VALIDATION_LIMITS.techDocMaxSourceLines;
        lines.push(
            `The uploaded project exceeds the maximum allowed limit of ${max.toLocaleString()} total source-code lines for ${PRODUCT_NAMES.TECH_DOC}.`,
        );
        lines.push(
            'Remove non-essential folders (for example `node_modules`, `bin`, `obj`, or generated output) and upload a smaller ZIP.',
        );
    } else if (status === 'LIMIT_REACHED') {
        const cap = isBrd
            ? VALIDATION_LIMITS.brdMaxGenerationsPerUser
            : VALIDATION_LIMITS.techDocMaxGenerationsPerUser;
        lines.push(
            `You have reached the maximum limit of ${cap} generations for ${productName}.`,
        );
    } else if (status === 'AUTH_REQUIRED') {
        lines.push(
            `A signed-in Clerk session is required for ${productName} generation.`,
            'Sign in and retry your request.',
        );
    } else {
        lines.push('The request could not be completed.');
    }
    const detail =
        (typeof parsed.message === 'string' && parsed.message.trim()) || lines[0] || '';
    const body = lines.filter(Boolean).join('\n\n');
    return `**ERROR** · ${label}\n\n${body || detail}`;
}

/** 3–4 lines under SUCCESS: uploads, outputs, and context (no download instructions). */
function buildSuccessNarrative(files, label, parsed, context) {
    const attachmentNames = Array.isArray(context?.attachmentNames) ? context.attachmentNames : [];
    const userPrompt = typeof context?.userPrompt === 'string' ? context.userPrompt.trim() : '';
    const formName =
        typeof parsed?.form_name === 'string' && parsed.form_name.trim() ? parsed.form_name.trim() : '';

    const parts = [];

    if (attachmentNames.length > 0) {
        parts.push(
            `Your upload included **${attachmentNames.join(', ')}** — that material was used as input for this run.`,
        );
    }

    if (formName) {
        parts.push(`Processing used form/project context: **${formName}**.`);
    }

    const names = Array.isArray(files) ? files.map((f) => f.filename).filter(Boolean) : [];
    if (names.length > 0) {
        const shown =
            names.length > 5
                ? `${names.slice(0, 5).join(', ')} (and ${names.length - 5} more)`
                : names.join(', ');
        parts.push(`Artifacts produced in this step include: **${shown}**.`);
    }

    if (userPrompt) {
        const short = userPrompt.length > 160 ? `${userPrompt.slice(0, 160)}…` : userPrompt;
        parts.push(`Your request was: “${short}”.`);
    }

    const closing = attachmentNames.length
        ? `This completes the **${label}** step; compare the outputs below with your uploaded inputs.`
        : `This completes the **${label}** step; review the generated results shown below.`;

    if (parts.length === 0) {
        return [`The **${label}** step finished successfully.`, closing].join('\n');
    }

    const body = parts.slice(0, 3);
    return [...body, closing].slice(0, 4).join('\n');
}

/** When the model echoes raw tool JSON, show a short summary instead of the full blob. */
function replaceRawToolJsonWithSummary(text, files, context) {
    if (!text) return text;
    const parsed = tryParseLeadingJson(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return text;
    if (!looksLikeToolResultJson(parsed)) return text;

    const banner = outcomeLabelFromToolStatus(parsed.status);
    const dt = parsed.diagram_type || parsed.prompt_name || '';
    const label = dt ? String(dt).replace(/_/g, ' ') : 'Tool response';

    const restrictionStatus = String(parsed.status || '').toUpperCase();
    if (
        restrictionStatus === 'LINE_LIMIT_EXCEEDED' ||
        restrictionStatus === 'LIMIT_REACHED' ||
        restrictionStatus === 'AUTH_REQUIRED' ||
        restrictionStatus === 'ZIP_FILE_COUNT_EXCEEDED' ||
        restrictionStatus === 'ZIP_SIZE_EXCEEDED' ||
        restrictionStatus === 'ZIP_EMPTY'
    ) {
        const mod =
            parsed.system === 'BRD' ? MODULE_TYPES.BRD : MODULE_TYPES.TECH_DOC;
        if (restrictionStatus === 'LINE_LIMIT_EXCEEDED' && mod === MODULE_TYPES.BRD) {
            return `**ERROR** · ${label}\n\nThe request could not be completed.`;
        }
        return formatGenerationRestrictionMessage(parsed, label, mod);
    }

    if (banner === 'SUCCESS') {
        const narrative = buildSuccessNarrative(files, label, parsed, context);
        return `**SUCCESS** · ${label}\n\n${narrative}`;
    }

    const detail =
        (typeof parsed.message === 'string' && parsed.message.trim()) ||
        (typeof parsed.error === 'string' && parsed.error.trim()) ||
        'The request could not be completed. Review the details below or try again.';
    return `**ERROR** · ${label}\n\n${detail}`;
}

/** Guess a short label for plain-text assistant errors (matches SUCCESS · … style). */
function inferErrorBannerLabel(text) {
    const t = String(text || '');
    if (/\bBRD\b/i.test(t)) return 'BRD documentation';
    if (/architecture\s+diagram/i.test(t)) return 'Architecture diagram';
    if (/technical\s+documentation/i.test(t)) return 'Technical documentation';
    if (/functional\s+documentation/i.test(t)) return 'Functional documentation';
    if (/database\s+schema/i.test(t)) return 'Database schema report';
    return 'Document generation';
}

/**
 * When the model returns prose (not JSON) but includes **Error:** mid-message,
 * promote a leading **ERROR** · banner so it matches the SUCCESS formatting.
 */
function ensureLeadingErrorBanner(text) {
    if (!text || typeof text !== 'string') return text;
    const trimmed = text.trim();
    if (/^\*\*SUCCESS\*\*/i.test(trimmed) || /^\*\*ERROR\*\*/i.test(trimmed)) {
        return text;
    }

    const lines = text.split('\n');
    let errIdx = -1;
    let errLineRaw = '';
    for (let i = 0; i < lines.length; i++) {
        const rawLine = lines[i] || '';
        const ln = rawLine.trim();
        // Common LLM patterns:
        // - "**Error:** ...", "**ERROR:** ..."
        // - "**Error Details:** ..." / "**error details:**"
        // - Plain "Error: ..." / "ERROR: ..."
        if (
            /^\*\*Error\*\*\s*:/i.test(ln) ||
            /^\*\*ERROR\*\*\s*:/i.test(ln) ||
            /^\*\*Error\s+Details\*\*\s*:/i.test(ln) ||
            /^\*\*Errors?\s*:\*\*\s*/i.test(ln) ||
            /^Error\s*:/i.test(ln) ||
            /^ERROR\s*:/i.test(ln)
        ) {
            errIdx = i;
            errLineRaw = ln;
            break;
        }
    }

    if (errIdx >= 0) {
        const restLines = [...lines.slice(0, errIdx), ...lines.slice(errIdx + 1)];
        const detailFromMarker = errLineRaw
            .replace(/^\*\*Error\*\*\s*:\s*/i, '')
            .replace(/^\*\*ERROR\*\*\s*:\s*/i, '')
            .replace(/^\*\*Error\s+Details\*\*\s*:\s*/i, '')
            .replace(/^Error\s*:\s*/i, '')
            .replace(/^ERROR\s*:\s*/i, '')
            .trim();
        const label = inferErrorBannerLabel(text);
        const body = restLines.join('\n').trim();
        const banner = `**ERROR** · ${label}`;
        const detailBlock = detailFromMarker ? `${detailFromMarker}\n\n${body}` : body;
        return `${banner}\n\n${detailBlock}`.trim();
    }

    // Fallback: BRD/config failures often never include "Error:" — only narrative + "missing" cues.
    const looksLikeFailure =
        /\b(document generation failed|configuration issue|missing (the )?required|prompt file not found|missing file)\b/i.test(
            trimmed,
        );
    const notSuccessNarrative = !/^\*\*SUCCESS\*\*/i.test(trimmed);
    if (notSuccessNarrative && looksLikeFailure) {
        const label = inferErrorBannerLabel(text);
        return `**ERROR** · ${label}\n\n${trimmed}`;
    }

    return text;
}

function suggestFilenameForCodeBlock(lang, index) {
    const raw = (lang || "").trim();
    if (!raw) return `snippet-${index + 1}.txt`;
    const baseOnly = raw.split(/[/\\]/).pop() || raw;
    if (/^[\w.-]+\.[A-Za-z0-9]{1,12}$/.test(baseOnly)) {
        return baseOnly.replace(/[<>:"|?*]/g, "_");
    }
    const extMap = {
        xml: "xml",
        json: "json",
        sql: "sql",
        plsql: "sql",
        javascript: "js",
        js: "js",
        typescript: "ts",
        ts: "ts",
        html: "html",
        htm: "html",
        md: "md",
        markdown: "md",
        yaml: "yaml",
        yml: "yml",
        py: "py",
        python: "py",
        sh: "sh",
        bash: "sh",
        plaintext: "txt",
        text: "txt",
        txt: "txt",
        csv: "csv",
    };
    const lower = raw.toLowerCase();
    if (extMap[lower]) return `snippet-${index + 1}.${extMap[lower]}`;
    return `snippet-${index + 1}.txt`;
}

/**
 * Shared chat UI for documentation modules. Upload, query, and rendering behavior
 * are unchanged; only empty-state copy and capability lists vary by module.
 *
 * @param {Object} props
 * @param {'tech-doc'|'brd'} [props.moduleType] - Preset from chatModuleConfig
 * @param {string} [props.pageTitle] - Header above chat (e.g. module name)
 * @param {string} [props.introTitle]
 * @param {string} [props.introDescription]
 * @param {string[]} [props.capabilities]
 * @param {string} [props.capabilitiesSectionTitle]
 * @param {string|null} [props.composerPlaceholder] - When set, used instead of "Type your message…"
 * @param {string} [props.emptyStateMessage]
 */
const ChatInterface = ({
    moduleType = MODULE_TYPES.TECH_DOC,
    pageTitle,
    introTitle,
    introDescription,
    capabilities,
    capabilitiesSectionTitle,
    composerPlaceholder,
    emptyStateMessage,
}) => {
    const moduleConfig = useMemo(
        () =>
            resolveChatModuleConfig({
                moduleType,
                pageTitle,
                introTitle,
                introDescription,
                capabilities,
                capabilitiesSectionTitle,
                composerPlaceholder,
                emptyStateMessage,
            }),
        [
            moduleType,
            pageTitle,
            introTitle,
            introDescription,
            capabilities,
            capabilitiesSectionTitle,
            composerPlaceholder,
            emptyStateMessage,
        ]
    );

    const {
        isClerkLoaded,
        isSignedIn,
        tokenReady,
        isSessionReady,
        waitForSessionToken,
    } = useClerkSessionReady();

    const [messages, setMessages] = useState(() => [
        createOnboardingChatMessage(moduleType, { quotaStatus: 'loading' }),
    ]);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [attachError, setAttachError] = useState('');
    const [isOpeningFilePicker, setIsOpeningFilePicker] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportContent, setReportContent] = useState('');
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [isLoadingReportContent, setIsLoadingReportContent] = useState(false);
    const [reportType, setReportType] = useState('metadata'); // 'metadata', 'html', 'ts', 'integrated', 'backend'
    const [reportTypes] = useState([]);
    const [activeFormName, setActiveFormName] = useState(null); // Form name for active conversion
    const [activeCheckpoints, setActiveCheckpoints] = useState(null); // Checkpoints for active form
    const [isLoadingCheckpoints, setIsLoadingCheckpoints] = useState(false);
    // messagesEndRef removed - now using direct container scrolling
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const loadingIntervalRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const checkpointPollIntervalRef = useRef(null);
    const recentCheckpointsPollIntervalRef = useRef(null);
    /** Bumped on each send; polling ignores results from older generations. */
    const generationEpochRef = useRef(0);
    /** Scoped per in-flight generation (module + timestamp). */
    const activeJobIdRef = useRef(null);
    /** True after first user message; enables auto-scroll-to-bottom for live chat. */
    const liveConversationRef = useRef(false);
    /** One-time scroll-to-top when onboarding-only (initial load per module). */
    const onboardingScrollInitializedRef = useRef(false);
    const prevMessagesForScrollRef = useRef(messages);
    /** Tracks BRD auto-prefill so we can update/clear without clobbering manual edits. */
    const brdPrefillRef = useRef('');

    const checkpointModuleType = moduleConfig.moduleType;
    const isBrdModule = checkpointModuleType === MODULE_TYPES.BRD;
    const isTechDocModule = checkpointModuleType === MODULE_TYPES.TECH_DOC;
    const checkpointsEnabled = isBrdModule || isTechDocModule;

    const patchOnboardingMessage = useCallback(
        (quotaState) => {
            const content = buildOnboardingMarkdown(checkpointModuleType, quotaState);
            setMessages((prev) => {
                const hasOnboarding = prev.some((m) => m.id === ONBOARDING_MESSAGE_ID);
                if (!hasOnboarding) {
                    return [
                        {
                            ...createOnboardingChatMessage(checkpointModuleType, quotaState),
                            content,
                        },
                        ...prev,
                    ];
                }
                return prev.map((m) =>
                    m.id === ONBOARDING_MESSAGE_ID ? { ...m, content } : m
                );
            });
        },
        [checkpointModuleType]
    );

    const loadOnboardingQuota = useCallback(async () => {
        if (!isClerkLoaded) {
            patchOnboardingMessage({
                authenticated: false,
                quotaStatus: 'loading',
            });
            return;
        }

        if (!isSignedIn) {
            patchOnboardingMessage({
                authenticated: false,
                quotaStatus: 'signed_out',
            });
            return;
        }

        if (!tokenReady) {
            patchOnboardingMessage({
                authenticated: true,
                quotaStatus: 'loading',
            });
            return;
        }

        patchOnboardingMessage({
            authenticated: true,
            quotaStatus: 'loading',
        });

        const fetchQuotaOnce = async () => {
            const token = await waitForSessionToken();
            if (!token) {
                return null;
            }
            return api.chat.getGenerationQuota(checkpointModuleType);
        };

        try {
            let quota = await fetchQuotaOnce();
            if (!quota) {
                patchOnboardingMessage({
                    authenticated: true,
                    quotaStatus: 'loading',
                });
                return;
            }
            patchOnboardingMessage({
                ...quota,
                authenticated: true,
                quotaStatus: 'loaded',
            });
        } catch (err) {
            const is401 = err?.status === 401;
            const notReady = err?.code === 'CLERK_TOKEN_NOT_READY';
            if (is401 || notReady) {
                const token = await waitForSessionToken();
                if (token) {
                    try {
                        const quota = await api.chat.getGenerationQuota(
                            checkpointModuleType
                        );
                        patchOnboardingMessage({
                            ...quota,
                            authenticated: true,
                            quotaStatus: 'loaded',
                        });
                        return;
                    } catch (retryErr) {
                        console.warn('[quota] Retry after token wait failed:', retryErr);
                    }
                }
                patchOnboardingMessage({
                    authenticated: true,
                    quotaStatus: 'loading',
                });
                return;
            }
            console.warn('[quota] Failed to load generation quota:', err);
            patchOnboardingMessage({
                authenticated: true,
                quotaStatus: 'error',
                quotaError: true,
            });
        }
    }, [
        checkpointModuleType,
        isClerkLoaded,
        isSignedIn,
        tokenReady,
        waitForSessionToken,
        patchOnboardingMessage,
    ]);

    const refreshOnboardingQuotaInChat = loadOnboardingQuota;

    useEffect(() => {
        if (!isClerkLoaded) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await loadOnboardingQuota();
        })();
        return () => {
            cancelled = true;
        };
    }, [isClerkLoaded, isSignedIn, tokenReady, isSessionReady, loadOnboardingQuota]);

    useEffect(() => {
        liveConversationRef.current = false;
        onboardingScrollInitializedRef.current = false;
    }, [checkpointModuleType]);

    // Progressive loading messages
    const loadingMessages = useMemo(
        () => [
            "Agent is analysing your request...",
            "Agent is converting your request...",
            "Agent is generating the code...",
            "Agent is finalizing the output...",
            "Agent is preparing the response...",
        ],
        []
    );

    const scrollToTop = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    const scrollToBottom = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth',
        });
    }, []);

    const hasUserMessages = useCallback(
        (msgs) => msgs.some((m) => m.type === 'user'),
        []
    );

    useEffect(() => {
        const prev = prevMessagesForScrollRef.current;
        prevMessagesForScrollRef.current = messages;

        const userStartedChat = hasUserMessages(messages);
        if (userStartedChat) {
            liveConversationRef.current = true;
        }

        const onlyOnboardingQuotaRefresh =
            prev.length === messages.length &&
            prev.every((m, i) => messages[i]?.id === m.id) &&
            (() => {
                const p = prev.find((m) => m.id === ONBOARDING_MESSAGE_ID);
                const n = messages.find((m) => m.id === ONBOARDING_MESSAGE_ID);
                return Boolean(p && n && p.content !== n.content);
            })();

        if (onlyOnboardingQuotaRefresh) {
            return undefined;
        }

        if (!liveConversationRef.current) {
            if (!onboardingScrollInitializedRef.current) {
                const frame = requestAnimationFrame(() => {
                    scrollToTop();
                    onboardingScrollInitializedRef.current = true;
                });
                return () => cancelAnimationFrame(frame);
            }
            return undefined;
        }

        scrollToBottom();
        return undefined;
    }, [messages, loadingMessage, hasUserMessages, scrollToTop, scrollToBottom]);

    useEffect(() => {
        return () => {
            generationEpochRef.current += 1;
            activeJobIdRef.current = null;
            if (checkpointPollIntervalRef.current) {
                clearInterval(checkpointPollIntervalRef.current);
            }
            if (recentCheckpointsPollIntervalRef.current) {
                clearInterval(recentCheckpointsPollIntervalRef.current);
            }
            if (loadingIntervalRef.current) {
                clearInterval(loadingIntervalRef.current);
            }
        };
    }, []);

    // After the first file is attached, move focus to the message box (typing is enabled then).
    useEffect(() => {
        if (pendingAttachments.length > 0) {
            inputRef.current?.focus();
        }
    }, [pendingAttachments.length]);

    // BRD has a single supported command — prefill it once input files are ready to attach.
    useEffect(() => {
        if (!isBrdModule) return;

        const readyNames = pendingAttachments
            .filter(
                (a) =>
                    (a.status || 'ready') === 'ready' &&
                    typeof a.content === 'string' &&
                    a.content.length > 0
            )
            .map((a) => a.filename);

        if (readyNames.length === 0) {
            if (brdPrefillRef.current) {
                setInputValue((prev) => (prev === brdPrefillRef.current ? '' : prev));
                brdPrefillRef.current = '';
            }
            return;
        }

        if (pendingAttachments.some((a) => a.status === 'loading')) return;

        const prefill = buildBrdPrefillCommand(readyNames);
        setInputValue((prev) => {
            if (!prev.trim() || prev === brdPrefillRef.current) return prefill;
            return prev;
        });
        brdPrefillRef.current = prefill;
    }, [isBrdModule, pendingAttachments]);

    // Handle scrollbar visibility during scroll
    useEffect(() => {
        const messagesContainer = messagesContainerRef.current;
        if (!messagesContainer) return;

        const handleScroll = () => {
            // Add scrolling class to show scrollbar
            messagesContainer.classList.add('scrolling');
            
            // Clear previous timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            
            // Hide scrollbar after scroll stops
            scrollTimeoutRef.current = setTimeout(() => {
                messagesContainer.classList.remove('scrolling');
            }, 1000); // Hide 1 second after scrolling stops
        };

        messagesContainer.addEventListener('scroll', handleScroll);
        
        return () => {
            messagesContainer.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Typing animation effect
    useEffect(() => {
        if (isTyping && isLoading && loadingMessageIndex < loadingMessages.length) {
            const message = loadingMessages[loadingMessageIndex];
            let currentIndex = 0;
            
            const typeChar = () => {
                if (currentIndex < message.length) {
                    setLoadingMessage(message.substring(0, currentIndex + 1));
                    currentIndex++;
                    typingTimeoutRef.current = setTimeout(typeChar, 30); // Typing speed
                } else {
                    setIsTyping(false);
                }
            };
            
            setLoadingMessage('');
            typeChar();
            
            return () => {
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }
            };
        }
    }, [isTyping, loadingMessageIndex, isLoading, loadingMessages]);

    // Progressive loading messages effect
    useEffect(() => {
        if (isLoading) {
            setLoadingMessageIndex(0);
            setIsTyping(true);
            
            // Change message every 45-60 seconds (adjust based on 5-minute average)
            loadingIntervalRef.current = setInterval(() => {
                setLoadingMessageIndex(prev => {
                    const nextIndex = prev + 1;
                    if (nextIndex < loadingMessages.length) {
                        setIsTyping(true);
                        return nextIndex;
                    }
                    return prev;
                });
            }, 50000); // Change message every 50 seconds
            
            return () => {
                if (loadingIntervalRef.current) {
                    clearInterval(loadingIntervalRef.current);
                }
            };
        } else {
            if (loadingIntervalRef.current) {
                clearInterval(loadingIntervalRef.current);
            }
            setLoadingMessage('');
            setLoadingMessageIndex(0);
            setIsTyping(false);
        }
    }, [isLoading, loadingMessages]);

    // Update loading message when index changes
    useEffect(() => {
        if (isLoading && loadingMessageIndex < loadingMessages.length) {
            setIsTyping(true);
        }
    }, [loadingMessageIndex, isLoading, loadingMessages]);

    // Function to load checkpoints for active form (module-scoped)
    const loadActiveCheckpoints = useCallback(async () => {
        if (!activeFormName || !checkpointsEnabled) return;

        const epoch = generationEpochRef.current;
        setIsLoadingCheckpoints(true);
        try {
            const data = await api.chat.getFormCheckpoints(
                activeFormName,
                checkpointModuleType,
                activeJobIdRef.current
            );
            if (epoch !== generationEpochRef.current) return;
            setActiveCheckpoints(data);
        } catch (err) {
            console.error('Error loading checkpoints:', err);
            // Don't show error to user, just log it
        } finally {
            if (epoch === generationEpochRef.current) {
                setIsLoadingCheckpoints(false);
            }
        }
    }, [activeFormName, checkpointModuleType, checkpointsEnabled]);

    // Poll for checkpoint updates every 10 seconds when active form is set (same module only)
    useEffect(() => {
        if (activeFormName && checkpointsEnabled) {
            // Load checkpoints immediately
            loadActiveCheckpoints();
            
            // Set up polling every 10 seconds
            checkpointPollIntervalRef.current = setInterval(() => {
                loadActiveCheckpoints();
            }, 10000); // 10 seconds

            return () => {
                if (checkpointPollIntervalRef.current) {
                    clearInterval(checkpointPollIntervalRef.current);
                }
            };
        } else {
            // Clear checkpoints when no active form
            setActiveCheckpoints(null);
            if (checkpointPollIntervalRef.current) {
                clearInterval(checkpointPollIntervalRef.current);
            }
        }
    }, [activeFormName, loadActiveCheckpoints, checkpointsEnabled]);

    // Poll for recently created checkpoints when generation is in progress (module-scoped)
    useEffect(() => {
        if (isLoading && checkpointsEnabled) {
            const checkForRecentCheckpoints = async () => {
                const epoch = generationEpochRef.current;
                try {
                    const data = await api.chat.getRecentCheckpoints(
                        5,
                        checkpointModuleType,
                        activeJobIdRef.current
                    );
                    if (epoch !== generationEpochRef.current) return;
                    if (data.recent_forms && data.recent_forms.length > 0) {
                        const mostRecentForm = data.recent_forms[0];
                        const formName = mostRecentForm.form_name;

                        if (!activeFormName) {
                            console.log(
                                `[checkpoints:${checkpointModuleType}] Using recent key:`,
                                formName
                            );
                            setActiveFormName(formName);
                            setIsLoadingCheckpoints(true);
                            try {
                                const checkpointData = await api.chat.getFormCheckpoints(
                                    formName,
                                    checkpointModuleType,
                                    activeJobIdRef.current
                                );
                                if (epoch !== generationEpochRef.current) return;
                                setActiveCheckpoints(checkpointData);
                            } catch (err) {
                                console.error('Error loading checkpoints:', err);
                            } finally {
                                if (epoch === generationEpochRef.current) {
                                    setIsLoadingCheckpoints(false);
                                }
                            }
                        } else {
                            console.log(
                                `[checkpoints:${checkpointModuleType}] Active key kept:`,
                                activeFormName
                            );
                        }
                    }
                } catch (err) {
                    console.error('Error checking for recent checkpoints:', err);
                }
            };

            checkForRecentCheckpoints();
            recentCheckpointsPollIntervalRef.current = setInterval(
                checkForRecentCheckpoints,
                10000
            );

            return () => {
                if (recentCheckpointsPollIntervalRef.current) {
                    clearInterval(recentCheckpointsPollIntervalRef.current);
                }
            };
        }
        if (recentCheckpointsPollIntervalRef.current) {
            clearInterval(recentCheckpointsPollIntervalRef.current);
        }
    }, [isLoading, activeFormName, checkpointModuleType, checkpointsEnabled]);

    const extractTechDocTrackingKey = (query, attachmentNames = []) => {
        const zip = attachmentNames.find((n) => /\.zip$/i.test(n));
        if (zip) {
            return zip.replace(/\.zip$/i, '').replace(/[^\w.-]+/g, '_').slice(0, 80);
        }
        const match = (query || '').match(/\b([A-Za-z0-9][\w.-]{2,})\b/);
        return match ? match[1].slice(0, 80) : null;
    };

    const shouldTrackCheckpointsForRequest = (query, responseData, attachmentNames) => {
        if (!checkpointsEnabled) return false;
        const cmdCheck = validateGenerationCommand({
            moduleType: checkpointModuleType,
            query: query || '',
            hasAttachments: (attachmentNames || []).length > 0,
            attachmentNames: attachmentNames || [],
        });
        if (!cmdCheck.allowed) return false;
        if (isBrdModule) {
            return Boolean(responseData?.form_name || extractFormName(query, responseData));
        }
        if (isTechDocModule) {
            return (attachmentNames || []).some((n) => /\.zip$/i.test(n)) || Boolean(
                extractTechDocTrackingKey(query, attachmentNames)
            );
        }
        return false;
    };

    // Helper function to extract form name from response or query
    const extractFormName = (query, responseData) => {
        // First try to get from response
        if (responseData?.form_name) {
            return responseData.form_name;
        }
        
        // Try to extract from response content
        const assistantContent = extractLastAssistantMessage(responseData);
        if (assistantContent) {
            // Look for form name patterns in the response - various formats
            const patterns = [
                /form[:\s]+([A-Z0-9_]+)/i,  // "form: DM001" or "form DM001"
                /converting\s+([A-Z0-9_]+)/i,  // "converting DM001"
                /([A-Z0-9_]+)\s+form/i,  // "DM001 form"
            ];
            
            for (const pattern of patterns) {
                const match = assistantContent.match(pattern);
                if (match && match[1]) {
                    return match[1];
                }
            }
        }
        
        // Try to extract from query - look for patterns like "convert DM001" or "generate brd for ebal013"
        const queryPatterns = [
            /generate\s+(?:brd|document|documents?)\s+(?:for|of)\s+([A-Z0-9_]+)/i,  // "generate brd for ebal013"
            /convert\s+([A-Z0-9_]+)/i,  // "convert DM001"
            /convert\s+([A-Z0-9_]+)\s+/i,  // "convert DM001 metadata"
            /form[:\s]+([A-Z0-9_]+)/i,  // "form: DM001" or "form DM001"
            /([A-Z0-9_]+)\s+metadata/i,  // "DM001 metadata"
            /for\s+form\s+([A-Z0-9_]+)/i,  // "for form DM001"
            /\b([A-Z]{2,}[A-Z0-9]{3,})\b/,  // Match any form-like pattern (e.g., EBAL013, DM001)
        ];
        
        for (const pattern of queryPatterns) {
            const match = query.match(pattern);
            if (match && match[1]) {
                return match[1].toUpperCase(); // Normalize to uppercase
            }
        }
        
        return null;
    };

    // Helper function to extract last assistant message from response
    const extractLastAssistantMessage = (responseData) => {
        if (!responseData?.messages) return null;
        
        // Find the last message with role "assistant"
        const assistantMessages = responseData.messages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length === 0) return null;
        
        const lastAssistant = assistantMessages[assistantMessages.length - 1];
        
        // If content is a string, return it directly
        if (typeof lastAssistant.content === 'string') {
            return lastAssistant.content;
        }
        
        // If content is an array, find the text type
        if (Array.isArray(lastAssistant.content)) {
            const textContent = lastAssistant.content.find(item => item.type === 'text');
            if (textContent?.text) {
                return textContent.text;
            }
        }
        
        return null;
    };

    const extractGeneratedFiles = (responseData) => {
        const list = responseData?.metadata?.generated_files;
        if (!Array.isArray(list)) return [];
        const seen = new Set();
        const files = [];
        for (const item of list) {
            if (!item || typeof item !== 'object') continue;
            const path = typeof item.path === 'string' ? item.path.trim() : '';
            if (!path || seen.has(path)) continue;
            const nameFromItem = typeof item.filename === 'string' ? item.filename.trim() : '';
            const filename =
                nameFromItem ||
                path.replace(/\\/g, '/').split('/').filter(Boolean).pop() ||
                'generated-file';
            seen.add(path);
            files.push({
                path,
                filename,
                tool: typeof item.tool === 'string' ? item.tool : '',
            });
        }
        return filterGeneratedFilesForModule(files, checkpointModuleType);
    };

    // Helper function to render markdown-like content
    const renderMarkdown = (text) => {
        if (!text) return '';
        
        // Split by lines and process markdown-like syntax
        const lines = text.split('\n');
        return lines.map((line, lineIndex) => {
            const lineKey = `line-${lineIndex}`;
            // Headers
            if (line.startsWith('## ')) {
                return <h2 key={lineKey} className="text-xl font-bold mt-4 mb-2">{line.substring(3)}</h2>;
            }
            if (line.startsWith('### ')) {
                return <h3 key={lineKey} className="text-lg font-semibold mt-3 mb-2">{line.substring(4)}</h3>;
            }
            // Bold text
            if (line.includes('**')) {
                const parts = line.split(/(\*\*.*?\*\*)/g);
                return (
                    <p key={lineKey} className="mb-2">
                        {parts.map((part, partIndex) => {
                            const partKey = `part-${lineIndex}-${partIndex}`;
                            if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={partKey}>{part.slice(2, -2)}</strong>;
                            }
                            return <span key={partKey}>{part}</span>;
                        })}
                    </p>
                );
            }
            // Code blocks
            if (line.startsWith('`') && line.endsWith('`')) {
                return <code key={lineKey} className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{line.slice(1, -1)}</code>;
            }
            // List items
            if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
                return <li key={lineKey} className="ml-4 mb-1">{line.trim().substring(2)}</li>;
            }
            // Numbered list
            if (/^\d+\.\s/.test(line.trim())) {
                return <li key={lineKey} className="ml-4 mb-1 list-decimal">{line.trim().replace(/^\d+\.\s/, '')}</li>;
            }
            // Empty line
            if (line.trim() === '') {
                return <br key={lineKey} />;
            }
            // Regular paragraph
            return <p key={lineKey} className="mb-2">{line}</p>;
        });
    };

    /** Renders assistant reply: prose via renderMarkdown; each ``` fenced block gets a download control only. */
    const renderAssistantMessageBody = (fullText) => {
        const parts = parseMarkdownFencedBlocks(fullText);
        let codeBlockIndex = 0;
        return parts.map((part, i) => {
            if (part.type === "text") {
                return (
                    <div key={`txt-${i}`} className="prose prose-sm max-w-none">
                        {renderMarkdown(part.value)}
                    </div>
                );
            }
            const fileName = suggestFilenameForCodeBlock(part.lang, codeBlockIndex);
            codeBlockIndex += 1;
            return (
                <div
                    key={`code-${i}`}
                    className="my-3 rounded-lg border overflow-hidden not-prose text-left"
                    style={{
                        borderColor: "var(--border-color)",
                        backgroundColor: "var(--bg-tertiary)",
                    }}
                >
                    <div
                        className="flex items-center justify-between gap-2 px-2 py-1.5 border-b text-xs"
                        style={{
                            borderColor: "var(--border-color)",
                            backgroundColor: "var(--bg-secondary)",
                            color: "var(--text-primary)",
                        }}
                    >
                        <span className="font-mono truncate flex-1 min-w-0" title={fileName}>
                            {fileName}
                        </span>
                        <button
                            type="button"
                            onClick={() => saveTextToLocalDisk(part.body, fileName)}
                            className="flex-shrink-0 rounded-md p-1.5 transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-400"
                            style={{ color: "var(--text-secondary)" }}
                            title="Download this file"
                            aria-label={`Download ${fileName}`}
                        >
                            <DocumentDownloadIcon className="h-4 w-4" aria-hidden />
                        </button>
                    </div>
                    <pre
                        className="p-3 text-xs overflow-x-auto max-h-80 overflow-y-auto m-0 font-mono"
                        style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-tertiary)" }}
                    >
                        <code>{part.body}</code>
                    </pre>
                </div>
            );
        });
    };

    const handleSend = async (e) => {
        e.preventDefault();
        const trimmed = inputValue.trim();
        const readyAttachments = pendingAttachments.filter(
            (a) =>
                (a.status || "ready") === "ready" &&
                typeof a.content === "string" &&
                a.content.length > 0
        );
        const hasLoadingAttachments = pendingAttachments.some((a) => a.status === "loading");
        if (hasLoadingAttachments) {
            setAttachError("Wait until all attachments finish loading.");
            return;
        }
        if (isLoading) return;
        if (!trimmed) {
            setAttachError(
                'Enter one of the supported operations from the assistant message above.'
            );
            return;
        }

        const attachmentNamesForValidation = readyAttachments.map((a) => a.filename);
        const cmdValidation = validateGenerationCommand({
            moduleType: checkpointModuleType,
            query: trimmed,
            hasAttachments: readyAttachments.length > 0,
            attachmentNames: attachmentNamesForValidation,
        });
        if (!cmdValidation.allowed) {
            const userMessage = {
                id: Date.now(),
                type: 'user',
                content: trimmed,
                attachmentNames: readyAttachments.map((a) => a.filename),
                timestamp: new Date(),
            };
            setMessages((prev) => [
                ...prev,
                userMessage,
                {
                    id: Date.now() + 1,
                    type: 'ai',
                    content: cmdValidation.assistantMessage,
                    timestamp: new Date(),
                },
            ]);
            setInputValue('');
            setAttachError('');
            return;
        }

        const attachmentPayload = readyAttachments.map((a) => {
            const row = {
                filename: a.filename,
                content: a.content,
            };
            if (a.contentEncoding === "base64") {
                row.content_encoding = "base64";
            }
            return row;
        });
        const attachmentNames = readyAttachments.map((a) => a.filename);
        const queryText = trimmed;

        let displayContent = trimmed;
        if (attachmentNames.length) {
            displayContent = trimmed
                ? `${trimmed}\n\nAttached: ${attachmentNames.join(", ")}`
                : `Attached: ${attachmentNames.join(", ")}`;
        }

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: displayContent,
            attachmentNames,
            timestamp: new Date(),
        };

        const attachmentsSnapshot = [...pendingAttachments];

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setAttachError('');
        // Reset input height and remove scrollbar
        if (inputRef.current) {
            inputRef.current.style.height = '48px';
            inputRef.current.classList.remove('show-scrollbar');
        }
        const requestEpoch = generationEpochRef.current + 1;
        generationEpochRef.current = requestEpoch;
        activeJobIdRef.current = `${checkpointModuleType}-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 9)}`;

        setIsLoading(true);
        setLoadingMessageIndex(0);
        setIsTyping(true);
        setActiveFormName(null);
        setActiveCheckpoints(null);

        try {
            const combinedForDetection = [queryText, ...attachmentNames].join(" ");
            const responseData = await api.chat.sendQuery(queryText, attachmentPayload, {
                moduleType: checkpointModuleType,
                jobId: activeJobIdRef.current,
            });
            if (requestEpoch !== generationEpochRef.current) return;

            setPendingAttachments([]);
            console.log('Backend Response:', responseData);

            if (shouldTrackCheckpointsForRequest(combinedForDetection, responseData, attachmentNames)) {
                let trackingKey = null;
                if (isBrdModule) {
                    trackingKey = extractFormName(combinedForDetection, responseData);
                } else if (isTechDocModule) {
                    trackingKey = extractTechDocTrackingKey(combinedForDetection, attachmentNames);
                }
                if (trackingKey) {
                    console.log(
                        `[checkpoints:${checkpointModuleType}] Tracking key from query:`,
                        trackingKey
                    );
                    setActiveFormName(trackingKey);
                } else {
                    console.log(
                        `[checkpoints:${checkpointModuleType}] No key in query; recent poll may apply`
                    );
                }
            }

            let assistantContent = extractLastAssistantMessage(responseData);
            let generatedFiles = extractGeneratedFiles(responseData);
            const fromAssistantJson = tryExtractGeneratedFilesFromAssistantText(
                assistantContent || '',
                checkpointModuleType
            );
            if (fromAssistantJson.length > 0) {
                const byPath = new Map(generatedFiles.map((f) => [f.path, f]));
                fromAssistantJson.forEach((f) => {
                    if (f.path && !byPath.has(f.path)) byPath.set(f.path, f);
                });
                generatedFiles = [...byPath.values()];
            }
            if (assistantContent) {
                assistantContent = replaceRawToolJsonWithSummary(assistantContent, generatedFiles, {
                    attachmentNames,
                    userPrompt: trimmed,
                });
                if (generatedFiles.length > 0) {
                    assistantContent = stripArtifactPathsFromAssistantText(assistantContent, generatedFiles);
                }
                // Prose errors sometimes include "**Error:** ..." mid-message — promote **ERROR** · banner like SUCCESS.
                if (!generatedFiles.length) {
                    assistantContent = ensureLeadingErrorBanner(assistantContent);
                }
            }

            if (assistantContent || generatedFiles.length > 0) {
                const aiMessage = {
                    id: Date.now() + 1,
                    type: 'ai',
                    content:
                        assistantContent ||
                        (generatedFiles.length > 0
                            ? `**SUCCESS** · Generated output\n\n${buildSuccessNarrative(generatedFiles, 'Generated output', {}, { attachmentNames, userPrompt: trimmed })}`
                            : ''),
                    generatedFiles,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, aiMessage]);
            } else {
                const errorMessage = {
                    id: Date.now() + 1,
                    type: 'ai',
                    content: 'Sorry, I could not process the response. Please try again.',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, errorMessage]);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setPendingAttachments(attachmentsSnapshot);
            const errorMessage = {
                id: Date.now() + 1,
                type: 'ai',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            if (requestEpoch === generationEpochRef.current) {
                setIsLoading(false);
                setLoadingMessage('');
                setLoadingMessageIndex(0);
                setIsTyping(false);
                setActiveFormName(null);
                setActiveCheckpoints(null);
                activeJobIdRef.current = null;
            }
            refreshOnboardingQuotaInChat();
        }
    };

    const handleKeyPress = (e) => {
        if (!hasAttachments) {
            e.preventDefault();
            if (!isLoading && pendingAttachments.length < MAX_CHAT_ATTACHMENTS) {
                setIsOpeningFilePicker(true);
                fileInputRef.current?.click();
                window.setTimeout(() => {
                    window.addEventListener(
                        'focus',
                        () => {
                            setIsOpeningFilePicker(false);
                        },
                        { once: true }
                    );
                }, 0);
            }
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend(e);
        }
    };

    /** Standard browser download (typically saves to the Downloads folder). One button per file. */
    const handleDownloadGeneratedFile = async (file) => {
        if (!file?.path) return;
        setAttachError('');
        try {
            await api.chat.downloadGeneratedFile(file.path, file.filename || 'generated-file');
        } catch (error) {
            console.error('Error downloading generated file:', error);
            setAttachError(error?.message || 'Failed to download generated file.');
        }
    };

    const removeAttachment = (id) => {
        setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
        setAttachError('');
    };

    /** Add files picked from the OS file dialog or drag-and-drop from the local machine. */
    const addLocalFiles = async (fileList) => {
        setAttachError('');
        // Snapshot File[] immediately — clearing `<input type="file">` empties a live FileList reference.
        const files = Array.from(fileList || []).filter((f) => f && f.size > 0);
        if (!files.length) {
            const raw = Array.from(fileList || []).filter(Boolean);
            if (raw.some((f) => f.size === 0)) {
                setAttachError("Empty files cannot be attached.");
            }
            return;
        }

        if (isLoading) return;

        const room = MAX_CHAT_ATTACHMENTS - pendingAttachments.length;
        if (room <= 0) {
            setAttachError(`You can attach up to ${MAX_CHAT_ATTACHMENTS} files. Remove one to add another.`);
            return;
        }

        const toAdd = files.slice(0, room);
        for (const file of toAdd) {
            const isZip = (file.name || "").toLowerCase().endsWith(".zip");
            const maxBytes = isZip ? MAX_CHAT_ZIP_BYTES : MAX_CHAT_FILE_BYTES;
            if (file.size > maxBytes) {
                setAttachError(
                    isZip
                        ? `"${file.name}" is too large (max ${Math.round(maxBytes / (1024 * 1024))} MB for .zip project archives).`
                        : `"${file.name}" is too large (max ${Math.round(MAX_CHAT_FILE_BYTES / 1024)} KB per file).`
                );
                continue;
            }
            const id = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 9)}`;
            setPendingAttachments((prev) => [
                ...prev,
                { id, filename: file.name, content: "", status: "loading", ...(isZip ? { kind: "zip" } : {}) },
            ]);
            try {
                if (isZip) {
                    const b64 = await readFileAsBase64(file);
                    setPendingAttachments((prev) =>
                        prev.map((a) =>
                            a.id === id
                                ? {
                                      id,
                                      filename: file.name,
                                      content: b64,
                                      status: "ready",
                                      contentEncoding: "base64",
                                      kind: "zip",
                                  }
                                : a
                        )
                    );
                } else {
                    const text = await readAttachmentAsText(file);
                    setPendingAttachments((prev) =>
                        prev.map((a) =>
                            a.id === id ? { id, filename: file.name, content: text, status: "ready" } : a
                        )
                    );
                }
            } catch (err) {
                setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
                const hint =
                    err && err.message
                        ? err.message
                        : `Could not read "${file.name}". Try PDF, DOCX, XLSX, PPTX, ODT, or plain text (XML, TXT, JSON, SQL, etc.).`;
                setAttachError(hint);
            }
        }
    };

    const handleFileInputChange = async (e) => {
        // Copy files before resetting the input, or the FileList becomes empty (paperclip would do nothing).
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        setIsOpeningFilePicker(false);
        await addLocalFiles(files);
    };

    const openAttachmentPicker = () => {
        if (isLoading || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS) return;
        setIsOpeningFilePicker(true);
        fileInputRef.current?.click();
        window.setTimeout(() => {
            window.addEventListener(
                'focus',
                () => {
                    setIsOpeningFilePicker(false);
                },
                { once: true }
            );
        }, 0);
    };

    const handleAttachDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isLoading) setIsDragOver(true);
    };

    const handleAttachDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleAttachDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (isLoading) return;
        const dropped = e.dataTransfer?.files;
        if (dropped?.length) await addLocalFiles(Array.from(dropped));
    };

    const handleCloseReportModal = () => {
        setShowReportModal(false);
        setSelectedReport(null);
        setReportContent('');
        setReportType('metadata');
    };

    const handleReportTypeChange = async (type) => {
        setReportType(type);
        setSelectedReport(null);
        setReportContent('');
        setIsLoadingReports(true);
        
        try {
            const reportList = await api.chat.listReports(type);
            setReports(reportList.reports || []);
        } catch (error) {
            console.error('Error fetching reports:', error);
            setReports([]);
        } finally {
            setIsLoadingReports(false);
        }
    };

    const handleSelectReport = async (report) => {
        setSelectedReport(report);
        setIsLoadingReportContent(true);
        
        try {
            const content = await api.chat.getReportByTypeAndForm(report.report_type, report.form_name, 'html');
            setReportContent(content);
        } catch (error) {
            console.error('Error fetching report content:', error);
            setReportContent(`<div style="padding: 20px; color: red;">Error loading report: ${error.message}</div>`);
        } finally {
            setIsLoadingReportContent(false);
        }
    };

    const hasAttachOverlay = pendingAttachments.length > 0 || !!attachError;
    const hasAttachments = pendingAttachments.length > 0;

    const readyAttachmentCount = pendingAttachments.filter(
        (a) =>
            (a.status || "ready") === "ready" &&
            typeof a.content === "string" &&
            a.content.length > 0
    ).length;
    const hasLoadingAttachmentsUi = pendingAttachments.some((a) => a.status === "loading");
    const canSendMessage =
        (inputValue.trim().length > 0 || readyAttachmentCount > 0) &&
        !isLoading &&
        !hasLoadingAttachmentsUi;

    return (
        <div className="flex flex-col h-[calc(100vh-120px)] max-h-none chat-container">
            {/* Header */}
            <div className="mb-4 pb-3 border-b chat-header" style={{ borderBottomColor: 'var(--border-color)' }}>
                <h2 className="page-title" style={{ color: 'var(--text-primary)', marginBottom: 0 }}>
                    {moduleConfig.pageTitle}
                </h2>
            </div>

            {/* Messages Container */}
            <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto mb-4 space-y-6 px-4 py-4 min-h-0 chat-messages auto-hide-scrollbar" 
                style={{backgroundColor: 'var(--bg-primary)'}}
            >
                {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`flex gap-4 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            {message.type === 'ai' && (
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 8V4H8"/>
                                        <rect width="16" height="12" x="4" y="8" rx="2"/>
                                        <path d="M2 14h2"/>
                                        <path d="M20 14h2"/>
                                        <path d="M15 13v2"/>
                                        <path d="M9 13v2"/>
                                    </svg>
                                </div>
                            )}
                            <div
                                className={`max-w-[75%] md:max-w-[65%] rounded-2xl px-4 py-3 shadow-sm ${
                                    message.type === 'user'
                                        ? 'message-user'
                                        : 'message-ai'
                                }`}
                                style={{
                                    backgroundColor: message.type === 'user' ? 'var(--blue-primary)' : 'var(--bg-secondary)',
                                    color: message.type === 'user' ? 'white' : 'var(--text-primary)',
                                    borderColor: message.type === 'ai' ? 'var(--border-color)' : 'transparent',
                                    borderWidth: message.type === 'ai' ? '1px' : '0',
                                    borderStyle: 'solid'
                                }}
                            >
                                <div
                                    className={`break-words leading-relaxed ${
                                        message.type === 'user' ? 'whitespace-pre-wrap' : ''
                                    }`}
                                >
                                    {message.type === 'ai' ? (
                                        renderAssistantMessageBody(message.content)
                                    ) : (
                                        message.content
                                    )}
                                </div>
                                {message.type === 'ai' &&
                                    Array.isArray(message.generatedFiles) &&
                                    message.generatedFiles.length > 0 && (
                                        <div
                                            className="mt-3 pt-3 space-y-1.5 border-t"
                                            style={{ borderColor: 'var(--border-color)' }}
                                        >
                                            <p
                                                className="text-xs font-medium"
                                                style={{ color: 'var(--text-secondary)' }}
                                            >
                                                Generated files
                                            </p>
                                            <p
                                                className="text-xs mt-1 leading-snug"
                                                style={{ color: 'var(--text-muted)' }}
                                            >
                                                Files from this run — download to your computer
                                            </p>
                                            {message.generatedFiles.map((file, idx) => (
                                                <div
                                                    key={`${message.id}-${file.path}-${idx}`}
                                                    className="flex items-center gap-2 rounded-md border px-2 py-1.5"
                                                    style={{
                                                        borderColor: 'var(--border-color)',
                                                        backgroundColor: 'var(--bg-tertiary)',
                                                    }}
                                                >
                                                    <span
                                                        className="text-xs font-medium truncate flex-1 min-w-0"
                                                        style={{ color: 'var(--text-primary)' }}
                                                    >
                                                        {file.filename}
                                                    </span>
                                                    {file.tool ? (
                                                        <span
                                                            className="text-[10px] uppercase shrink-0 opacity-70 hidden sm:inline"
                                                            style={{ color: 'var(--text-muted)' }}
                                                        >
                                                            {file.tool}
                                                        </span>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownloadGeneratedFile(file)}
                                                        className="text-xs inline-flex items-center gap-1 rounded px-2 py-1 border hover:opacity-85 shrink-0"
                                                        style={{
                                                            color: 'var(--text-secondary)',
                                                            borderColor: 'var(--border-color)',
                                                        }}
                                                        title={`Download ${file.filename}`}
                                                    >
                                                        <DocumentDownloadIcon className="h-3.5 w-3.5" aria-hidden />
                                                        Download
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                <div
                                    className="text-xs mt-2 opacity-70"
                                    style={{
                                        color: message.type === 'user' ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)'
                                    }}
                                >
                                    {message.timestamp.toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>
                            {message.type === 'user' && (
                                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white" style={{backgroundColor: 'var(--blue-primary)'}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                        <circle cx="12" cy="7" r="4"/>
                                    </svg>
                                </div>
                            )}
                        </div>
                    ))
                }
                {isLoading && (
                    <>
                        <div className="flex justify-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 8V4H8"/>
                                    <rect width="16" height="12" x="4" y="8" rx="2"/>
                                    <path d="M2 14h2"/>
                                    <path d="M20 14h2"/>
                                    <path d="M15 13v2"/>
                                    <path d="M9 13v2"/>
                                </svg>
                            </div>
                            <div 
                                className="loading-indicator rounded-2xl px-4 py-3 max-w-[75%] md:max-w-[65%] shadow-sm"
                                style={{
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    borderColor: 'var(--border-color)',
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                            >
                                <div className="flex items-center space-x-2">
                                    <div className="flex space-x-1">
                                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted)', animationDelay: '0ms' }}></div>
                                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted)', animationDelay: '150ms' }}></div>
                                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted)', animationDelay: '300ms' }}></div>
                                    </div>
                                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                        {loadingMessage || loadingMessages[loadingMessageIndex] || 'Thinking...'}
                                        {isTyping && <span className="inline-block w-1 h-4 ml-1 animate-pulse" style={{ backgroundColor: 'var(--text-muted)' }}>|</span>}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* Module-scoped checkpoint progress (BRD or Tech Doc only) */}
                        {checkpointsEnabled && activeFormName && isLoading && (
                            <div className="flex justify-start gap-4 mt-4">
                                <div className="flex-shrink-0 w-8 h-8"></div>
                                <div 
                                    className="rounded-2xl px-4 py-3 max-w-[75%] md:max-w-[65%] shadow-sm"
                                    style={{
                                        backgroundColor: 'var(--bg-primary)',
                                        borderColor: 'var(--border-color)',
                                        borderWidth: '1px',
                                        borderStyle: 'solid'
                                    }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                            {isBrdModule ? 'BRD Generation Progress' : 'Documentation Progress'}:{' '}
                                            {activeCheckpoints?.form_name || activeFormName}
                                        </h4>
                                        {isLoadingCheckpoints && (
                                            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                                Updating...
                                            </div>
                                        )}
                                    </div>
                                    {isLoadingCheckpoints && !activeCheckpoints ? (
                                        <div className="text-xs py-2" style={{ color: 'var(--text-secondary)' }}>
                                            Loading checkpoints...
                                        </div>
                                    ) : activeCheckpoints && activeCheckpoints.checkpoints && Object.keys(activeCheckpoints.checkpoints).length > 0 ? (
                                        <div className="space-y-2">
                                            {Object.values(activeCheckpoints.checkpoints).map((checkpoint) => {
                                                const isCompleted = checkpoint.completed;
                                                return (
                                                    <div
                                                        key={checkpoint.id}
                                                        className={`flex items-center gap-2 text-xs ${!isCompleted ? 'checkpoint-pending' : ''}`}
                                                        style={{
                                                            animation: !isCompleted ? 'checkpointBlink 1.5s ease-in-out infinite' : 'none'
                                                        }}
                                                    >
                                                        <div style={{ color: isCompleted ? '#10b981' : '#6b7280' }}>
                                                            {isCompleted ? (
                                                                <IoCheckmarkCircle className="w-4 h-4" />
                                                            ) : (
                                                                <IoCloseCircle className="w-4 h-4" />
                                                            )}
                                                        </div>
                                                        <span 
                                                            className="flex-1 truncate"
                                                            style={{ 
                                                                color: isCompleted ? 'var(--text-primary)' : 'var(--text-secondary)',
                                                            }}
                                                        >
                                                            {checkpoint.name}
                                                        </span>
                                                        <span 
                                                            className="text-xs font-medium"
                                                            style={{ color: isCompleted ? '#10b981' : '#6b7280' }}
                                                        >
                                                            {isCompleted ? 'Completed' : 'Pending'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-xs py-2" style={{ color: 'var(--text-secondary)' }}>
                                            Checkpoints will appear here once the conversion starts...
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
                {/* Scroll marker removed - using direct container scroll */}
            </div>

            {/* Input Area */}
            <div 
                className="chat-input-area pt-3 pb-1"
                style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderTopColor: 'var(--border-color)',
                    borderTopWidth: '1px',
                    borderTopStyle: 'solid'
                }}
                onDragOver={handleAttachDragOver}
                onDragLeave={handleAttachDragLeave}
                onDrop={handleAttachDrop}
            >
                <form
                    onSubmit={handleSend}
                    className="flex items-end gap-2 max-w-4xl mx-auto px-4"
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        multiple
                        onChange={handleFileInputChange}
                    />
                    <div
                        role="group"
                        aria-label="Message and attachments"
                        className={`flex flex-col flex-1 min-w-0 rounded-xl border overflow-hidden transition-colors ${
                            isDragOver ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[var(--bg-secondary)]' : ''
                        }`}
                        style={{
                            borderColor: 'var(--border-color)',
                            backgroundColor: 'var(--bg-tertiary)',
                        }}
                    >
                        {hasAttachOverlay && (
                            <div
                                className="shrink-0 px-3 py-2 border-b max-h-[140px] overflow-y-auto"
                                style={{
                                    borderColor: 'var(--border-color)',
                                    backgroundColor: 'var(--bg-secondary)',
                                }}
                                data-testid="chat-attachment-strip"
                            >
                                {attachError && (
                                    <div
                                        className="text-xs leading-snug mb-2 last:mb-0"
                                        style={{ color: '#b91c1c' }}
                                    >
                                        {attachError}
                                    </div>
                                )}
                                {pendingAttachments.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 items-center">
                                        {pendingAttachments.map((a) => (
                                            <span
                                                key={a.id}
                                                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs border shadow-sm"
                                                style={{
                                                    backgroundColor: 'var(--bg-tertiary)',
                                                    color: 'var(--text-primary)',
                                                    borderColor: 'var(--border-color)',
                                                }}
                                            >
                                                {a.status === 'loading' && (
                                                    <span
                                                        className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-solid border-[var(--text-muted)] border-t-transparent"
                                                        aria-hidden
                                                    />
                                                )}
                                                <span
                                                    className="max-w-[min(220px,55vw)] truncate font-medium"
                                                    title={a.filename}
                                                >
                                                    {a.filename}
                                                </span>
                                                {a.kind === 'zip' && a.status === 'ready' && (
                                                    <span
                                                        className="text-[10px] shrink-0 rounded px-1 py-0 border"
                                                        style={{
                                                            color: 'var(--text-secondary)',
                                                            borderColor: 'var(--border-color)',
                                                        }}
                                                        title="Server extracts this ZIP into source_projects/_uploads/… like a normal project folder"
                                                    >
                                                        project .zip
                                                    </span>
                                                )}
                                                {a.status === 'loading' && (
                                                    <span
                                                        className="text-[10px] uppercase tracking-wide shrink-0"
                                                        style={{ color: 'var(--text-muted)' }}
                                                    >
                                                        {a.kind === 'zip' ? 'Loading…' : 'Reading…'}
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => removeAttachment(a.id)}
                                                    className="p-0.5 rounded hover:opacity-80"
                                                    style={{ color: 'var(--text-secondary)' }}
                                                    aria-label={`Remove ${a.filename}`}
                                                >
                                                    <IoClose className="w-4 h-4" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="relative min-h-[48px] z-0">
                            <button
                                type="button"
                                onClick={openAttachmentPicker}
                                disabled={
                                    isLoading || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS
                                }
                                title={`Attach files — documents as text (250 KB each); project .zip up to ${VALIDATION_LIMITS.maxZipSizeMb} MB with max ${VALIDATION_LIMITS.maxZipFileCount} files inside`}
                                aria-label="Attach files from your computer"
                                className="absolute left-2 top-3 z-10 p-1.5 rounded-lg transition-colors focus:outline-none focus:ring-2"
                                style={{
                                    color: 'var(--text-secondary)',
                                    cursor:
                                        isLoading || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS
                                            ? 'not-allowed'
                                            : 'pointer',
                                    opacity:
                                        isLoading || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS
                                            ? 0.45
                                            : 1,
                                }}
                            >
                                {isOpeningFilePicker && !hasAttachments ? (
                                    <span
                                        className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-[var(--text-secondary)] border-t-transparent"
                                        aria-hidden
                                    />
                                ) : (
                                    <PaperClipIcon className="h-5 w-5" aria-hidden />
                                )}
                            </button>
                            <textarea
                                ref={inputRef}
                                value={inputValue}
                                onChange={(e) => {
                                    setInputValue(e.target.value);
                                    e.target.style.height = 'auto';
                                    const newHeight = Math.min(e.target.scrollHeight, 200);
                                    e.target.style.height = newHeight + 'px';

                                    if (newHeight > 72) {
                                        e.target.classList.add('show-scrollbar');
                                    } else {
                                        e.target.classList.remove('show-scrollbar');
                                    }
                                }}
                                onKeyPress={handleKeyPress}
                                onClick={() => {
                                    if (!hasAttachments && !isLoading && pendingAttachments.length < MAX_CHAT_ATTACHMENTS) {
                                        openAttachmentPicker();
                                    }
                                }}
                                placeholder={
                                    !hasAttachments
                                        ? isOpeningFilePicker
                                            ? 'Opening file picker...'
                                            : 'Attach your file here'
                                        : isDragOver
                                          ? 'Drop files here…'
                                          : hasLoadingAttachmentsUi
                                            ? 'Attachments loading…'
                                            : moduleConfig.composerPlaceholder || 'Type your message…'
                                }
                                rows={1}
                                className={`chat-input relative z-0 w-full pl-11 pr-4 py-3 focus:outline-none focus:ring-2 resize-none overflow-y-auto border-0 ${
                                    hasAttachOverlay ? 'rounded-t-none rounded-b-xl' : 'rounded-xl'
                                } ${!hasAttachments ? 'cursor-pointer' : isLoading ? 'cursor-not-allowed' : ''}`}
                                style={{
                                    minHeight: '48px',
                                    maxHeight: '200px',
                                    backgroundColor: 'var(--bg-tertiary)',
                                    color: 'var(--text-primary)',
                                    opacity: isLoading ? 0.72 : 1,
                                }}
                                disabled={isLoading}
                                readOnly={!hasAttachments}
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={!canSendMessage}
                        className="chat-send-button flex-shrink-0 self-end h-[50px] px-6 rounded-xl font-medium transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 shadow-sm hover:shadow-md"
                        style={{
                            backgroundColor: !canSendMessage
                                ? 'var(--bg-tertiary)'
                                : 'var(--blue-primary)',
                            color: !canSendMessage ? 'var(--text-muted)' : 'white',
                            cursor: !canSendMessage ? 'not-allowed' : 'pointer',
                            focusRingColor: 'var(--blue-primary)',
                        }}
                    >
                        <IoSend className="w-5 h-5" />
                    </button>
                </form>
                <p className="text-xs mt-2 text-center" style={{ color: 'var(--text-muted)' }}>
                    {
                        "Attach at least one file, then type your message · Each generated file has its own Download button"
                    }
                </p>
            </div>

            {/* Report Modal */}
            {showReportModal && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                    onClick={handleCloseReportModal}
                >
                    <div 
                        className="flex flex-col bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh]"
                        style={{ backgroundColor: 'var(--bg-primary)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-4 border-b" style={{ borderBottomColor: 'var(--border-color)' }}>
                            <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                                Conversion Test Reports
                            </h3>
                            <button
                                onClick={handleCloseReportModal}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                style={{ color: 'var(--text-primary)' }}
                            >
                                <IoClose className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Report Type Tabs */}
                        <div className="flex border-b overflow-x-auto" style={{ borderBottomColor: 'var(--border-color)' }}>
                            {(() => {
                                // Helper function to get display name for report type
                                const getReportTypeDisplayName = (type) => {
                                    const displayNames = {
                                        'metadata': 'Metadata',
                                        'html': 'HTML',
                                        'ts': 'TypeScript',
                                        'integrated': 'Integrated UI',
                                        'backend': 'Backend'
                                    };
                                    return displayNames[type] || type.charAt(0).toUpperCase() + type.slice(1);
                                };

                                if (reportTypes.length > 0) {
                                    return reportTypes.map((typeInfo) => {
                                        const type = typeInfo.type;
                                        const isActive = reportType === type;
                                        return (
                                            <button
                                                key={type}
                                                onClick={() => handleReportTypeChange(type)}
                                                className={`flex-shrink-0 px-6 py-3 font-medium transition-all duration-200 ${
                                                    isActive ? 'border-b-2' : ''
                                                }`}
                                                style={{
                                                    color: isActive 
                                                        ? 'var(--blue-primary)' 
                                                        : 'var(--text-secondary)',
                                                    borderBottomColor: isActive 
                                                        ? 'var(--blue-primary)' 
                                                        : 'transparent',
                                                }}
                                                title={typeInfo.description}
                                            >
                                                {getReportTypeDisplayName(type)}
                                            </button>
                                        );
                                    });
                                } else {
                                    return (
                                // Fallback tabs if types not loaded
                                <>
                                    <button
                                        onClick={() => handleReportTypeChange('metadata')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'metadata' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'metadata' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'metadata' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        Metadata
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('html')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'html' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'html' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'html' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        HTML
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('ts')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'ts' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'ts' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'ts' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        TypeScript
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('integrated')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'integrated' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'integrated' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'integrated' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        Integrated UI
                                    </button>
                                    <button
                                        onClick={() => handleReportTypeChange('backend')}
                                        className={`flex-1 px-6 py-3 font-medium transition-all duration-200 ${
                                            reportType === 'backend' ? 'border-b-2' : ''
                                        }`}
                                        style={{
                                            color: reportType === 'backend' 
                                                ? 'var(--blue-primary)' 
                                                : 'var(--text-secondary)',
                                            borderBottomColor: reportType === 'backend' 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                        }}
                                    >
                                        Backend
                                    </button>
                                        </>
                                    );
                                }
                            })()}
                        </div>

                        {/* Modal Content */}
                        <div className="flex flex-1 overflow-hidden">
                            {/* Report List Sidebar */}
                            <div 
                                className="w-64 border-r overflow-y-auto p-4"
                                style={{ 
                                    borderRightColor: 'var(--border-color)',
                                    backgroundColor: 'var(--bg-secondary)'
                                }}
                            >
                                {isLoadingReports ? (
                                    <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                                        Loading reports...
                                    </div>
                                ) : reports.length === 0 ? (
                                    <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                                        No reports found
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {reports.map((report) => {
                                            const isSelected = selectedReport && 
                                                selectedReport.form_name === report.form_name && 
                                                selectedReport.report_type === report.report_type;
                                            return (
                                                <button
                                                    key={`${report.report_type}-${report.form_name}`}
                                                    onClick={() => handleSelectReport(report)}
                                                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                                                        isSelected
                                                            ? 'font-semibold'
                                                            : 'hover:bg-gray-100'
                                                    }`}
                                                    style={{
                                                        backgroundColor: isSelected 
                                                            ? 'var(--blue-primary)' 
                                                            : 'transparent',
                                                        color: isSelected 
                                                            ? 'white' 
                                                            : 'var(--text-primary)',
                                                    }}
                                                    title={report.exists ? `Report exists for ${report.form_name}` : 'Report not found'}
                                                >
                                                    <div className="flex flex-col">
                                                        <span>{report.form_name}</span>
                                                        {report.exists && (
                                                            <span className="text-xs opacity-75 mt-1">
                                                                {report.html_path ? 'HTML' : ''}
                                                                {report.html_path && report.json_path ? ' • ' : ''}
                                                                {report.json_path ? 'JSON' : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Report Content Area */}
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {selectedReport ? (
                                    <>
                                        {isLoadingReportContent ? (
                                            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                                Loading report content...
                                            </div>
                                        ) : selectedReport.exists ? (
                                            <iframe
                                                srcDoc={reportContent}
                                                className="w-full h-full border-0"
                                                title={`${selectedReport.form_name} - ${selectedReport.report_type}`}
                                                style={{ backgroundColor: 'white' }}
                                            />
                                        ) : (
                                            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                                Report not found for {selectedReport.form_name} ({selectedReport.report_type})
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                        Select a report from the list to view
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatInterface;

